// Fixed print tokens; page dimensions live in printoutPageGeometry.

export interface PrintoutTypeToken {
    fontSize: number;
    lineHeight: number;
    fontWeight: number;
    letterSpacing?: string;
    textTransform?: "uppercase";
}

export interface PrintoutTypeRamp {
    displayTitle: PrintoutTypeToken;
    largeValue: PrintoutTypeToken;
    detailValue: PrintoutTypeToken;
    h1: PrintoutTypeToken;
    h2: PrintoutTypeToken;
    h3: PrintoutTypeToken;
    h4: PrintoutTypeToken;
    body1: PrintoutTypeToken;
    body2: PrintoutTypeToken;
    caption: PrintoutTypeToken;
    label: PrintoutTypeToken;
    tableValue: PrintoutTypeToken;
}

export interface PrintoutRuleTokens {
    strong: string;
    default: string;
    light: string;
    width: number;
}

export interface PrintoutInkTokens {
    strong: string;
    body: string;
    muted: string;
}

export interface PrintoutFillTokens {
    band: string;
    paper: string;
}

export interface PrintoutTableTokens {
    rowPaddingY: number;
    columnGap: number;
    headerPaddingBottom: number;
    totalsWidth: string;
    totalsRowPaddingY: number;
    totalsMarginTop: number;
    groupHeaderPaddingTop: number;
    groupHeaderPaddingBottom: number;
}

export interface PrintoutTotalStepTokens {
    fontSize: number;
    fontWeight: number;
    letterSpacing?: string;
    marginTop: number;
    paddingTop: number;
    ruleTone: keyof Omit<PrintoutRuleTokens, "width">;
}

export interface PrintoutTheme {
    ink: PrintoutInkTokens;
    rule: PrintoutRuleTokens;
    fill: PrintoutFillTokens;
    type: PrintoutTypeRamp;
    measure: number;
    table: PrintoutTableTokens;
    grandTotal: PrintoutTotalStepTokens;
    settledTotal: PrintoutTotalStepTokens;
}

export const printoutTheme: PrintoutTheme = {
    ink: {
        strong: "#17181A",
        body: "#454A4E",
        muted: "#606866",
    },
    rule: {
        strong: "#17181A",
        default: "#B7BBBE",
        light: "#E2E4E5",
        width: 1,
    },
    fill: {
        band: "#F3F2EE",
        paper: "#FFFFFF",
    },
    type: {
        // CSS pixels at 96 dpi: one print point is 4/3 px.
        displayTitle: {fontSize: 36 * 4 / 3, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.1},
        largeValue: {fontSize: 32 * 4 / 3, fontWeight: 400, letterSpacing: "-0.035em", lineHeight: 1.1},
        detailValue: {fontSize: 13.5 * 4 / 3, fontWeight: 400, lineHeight: 1.5},
        h1: {fontSize: 23, fontWeight: 600, letterSpacing: "-0.016em", lineHeight: 1.18},
        h2: {fontSize: 16, fontWeight: 600, letterSpacing: "-0.008em", lineHeight: 1.22},
        h3: {fontSize: 14, fontWeight: 600, lineHeight: 1.3},
        h4: {fontSize: 12, fontWeight: 600, lineHeight: 1.4},
        body1: {fontSize: 11 * 4 / 3, fontWeight: 400, lineHeight: 1.5},
        body2: {fontSize: 9.5 * 4 / 3, fontWeight: 400, lineHeight: 1.5},
        caption: {fontSize: 9 * 4 / 3, fontWeight: 400, lineHeight: 1.5},
        label: {fontSize: 9 * 4 / 3, fontWeight: 500, lineHeight: 1.4},
        tableValue: {fontSize: 12, fontWeight: 400, lineHeight: 1.38},
    },
    measure: 66,
    table: {
        rowPaddingY: 6,
        columnGap: 14,
        headerPaddingBottom: 6,
        totalsWidth: "48%",
        totalsRowPaddingY: 1.5,
        totalsMarginTop: 11,
        groupHeaderPaddingTop: 10,
        groupHeaderPaddingBottom: 4,
    },
    grandTotal: {
        fontSize: 19,
        fontWeight: 600,
        letterSpacing: "-0.02em",
        marginTop: 7,
        paddingTop: 9,
        ruleTone: "strong",
    },
    settledTotal: {
        fontSize: 13,
        fontWeight: 600,
        marginTop: 7,
        paddingTop: 6,
        ruleTone: "light",
    },
};

export const printoutTypeSx = (token: PrintoutTypeToken) => ({
    fontSize: `${token.fontSize}px`,
    fontWeight: token.fontWeight,
    lineHeight: token.lineHeight,
    ...(token.letterSpacing ? {letterSpacing: token.letterSpacing} : {}),
    ...(token.textTransform ? {textTransform: token.textTransform} : {}),
});

export const printoutRule = (tone: keyof Omit<PrintoutRuleTokens, "width">) => (
    `${printoutTheme.rule.width}px solid ${printoutTheme.rule[tone]}`
);
